<template>
  <div
    :style="{
      top: `calc(${resultsHeaderHeight}px + var(--status-bar-height))`,
    }"
  >
    <MiniOfferFilters
      :is-filter-applied="isFilterAppliedPredicate"
      :applied-filter-amount="appliedFilterAmount"
      :is-filtered-offers-empty="filteredOffers.length === 0"
      @onOpenFilters="onOpenFilters"
    >
      <template #offer-sort-button>
        <slot name="offer-sort" />
      </template>
    </MiniOfferFilters>
    <FiltersBottomSheet
      :offers="offers"
      :filter-limits="filters.limits.value"
      :state="filters.state.value"
      :airlines="dict.airlines"
      :routes="routes"
      :filtered-offers="filteredOffers"
      :name="filtersBottomSheet.name"
      :filter-stage="filterStage"
      @onPriceChange="updatePricesFilter"
      @onFlightsFilterChange="updateFlightFilter"
      @onTransferFilterChange="updateTransferFilter"
      @onNightTransferFilterChange="updateNightTransferFilter"
      @onAirlinesFilterChange="updateAirlinesFilter"
      @onFlightDurationUpdate="updateFlightDuration"
      @onClearTransferFilter="clearTransferFilter"
      @onClearAirlinesFilter="clearAirlinesFilter"
      @onClearTimeFilter="clearTimeFilter"
      @onClearAllFilter="initFiltersState"
      @onClose="resetSelectedFilters"
      @onCommit="commitFilters"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeMount, ref, onMounted } from 'vue';
  import { useRoute } from 'vue-router';
  import { flightTimeFilter, airlineFilters } from 'shared';
  import { flightEntity } from 'freedom-shared';
  import {
    type FlightFilterState,
    setAllAirlines,
    getTransfersDurationRange,
  } from '@aviata/offers-filters-fns';
  import type { Dict } from '@aviata/platform-types';

  import {
    searchOffersLib,
    searchOffersModel,
  } from '@/features/search-offers-filter';

  import { sentryModel } from '@/shared/model';
  import { analyticsEventsLib } from '@/shared/lib';

  import { useFilterStage, filtersBottomSheetController } from '../model';
  import { filtersConfig, type FilterStage } from '../config';
  import {
    getAppliedFiltersParams,
    getAirlinesSeparateAppliedParams,
    getTimeSeparateAppliedParams,
    getTransfersSeparateAppliedParams,
    type ExtendedFilterState,
  } from '../lib';
  import FiltersBottomSheet from './FiltersBottomSheet.vue';
  import MiniOfferFilters from './MiniOfferFilters.vue';

  const props = defineProps<{
    offers: flightEntity.types.MappedOffer[];
    dict: Dict.Entity;
  }>();

  const emit = defineEmits<{
    onOffersFilter: [
      offers: Array<flightEntity.types.MappedOffer>,
      filterParams: Record<string, unknown>,
      hasFilter: boolean,
    ];
  }>();

  const route = useRoute();

  const resultsHeaderHeight = ref(0);

  const filtersBottomSheet = filtersBottomSheetController();
  const filterStage = useFilterStage();
  const openedFromMainFilters = ref(false);

  const filters = searchOffersModel.useOffersFilters(props.dict.airlines);
  const routes = computed(() =>
    searchOffersLib.getFlightsRoutes(props.offers[0]),
  );
  const committedFilterState = ref(filters.state.value);
  const filteredOffers = computed(() => filters.filterOffers(props.offers));
  const appliedFilterAmount = computed(
    () =>
      searchOffersLib.checkAppliedFilters({
        state: committedFilterState.value,
        limits: filters.limits.value,
      }).appliedFilterAmount,
  );

  const hasFilter = computed(
    () =>
      searchOffersLib.checkAppliedFilters({
        state: committedFilterState.value,
        limits: filters.limits.value,
      }).isAnyFilterApplied,
  );

  const filterParams = computed(() => {
    return getAppliedFiltersParams({
      state: committedFilterState.value,
      limits: filters.limits.value,
    });
  });

  const isFilterAppliedPredicate = (stage: FilterStage) => {
    switch (stage) {
      case filtersConfig.FILTER_STAGES.AIRLINES:
        return searchOffersLib.isAirlinesFilterApplied({
          state: filters.state.value,
        });
      case filtersConfig.FILTER_STAGES.TIME:
        return searchOffersLib.isFlightTimeFiltersApplied({
          state: filters.state.value,
        });
      case filtersConfig.FILTER_STAGES.TRANSFERS:
        return searchOffersLib.isTransferFiltersApplied({
          state: filters.state.value,
          limits: filters.limits.value,
        });
      default:
        return searchOffersLib.checkAppliedFilters({
          state: filters.state.value,
          limits: filters.limits.value,
        }).isAnyFilterApplied;
    }
  };

  const onOpenFilters = (stage?: FilterStage) => {
    if (stage) {
      openedFromMainFilters.value = false;
      filterStage.setFilterStage(stage);
      switch (stage) {
        case filtersConfig.FILTER_STAGES.AIRLINES:
          analyticsEventsLib.airlinesSeparateClick();
          break;
        case filtersConfig.FILTER_STAGES.TIME:
          analyticsEventsLib.timeSeparateClick();
          break;
        case filtersConfig.FILTER_STAGES.TRANSFERS:
          analyticsEventsLib.transfersSeparateClick();
          break;
      }
    } else {
      openedFromMainFilters.value = true;
      analyticsEventsLib.filtersClick();
    }
    filtersBottomSheet.show();
  };

  const initFiltersState = () => {
    filters.init({
      offers: props.offers,
      flightsNumber: routes.value.length,
    });
  };

  const updateNightTransferFilter = (isWithoutNightTransfer: boolean) => {
    // @ts-expect-error type in lib is not extended, but filter state has that field
    filters.onFilterUpdate({ isWithoutNightTransfer });
  };

  const updatePricesFilter = (newPrices: number[]) =>
    filters.onFilterUpdate({ prices: newPrices });

  const updateFlightFilter = (
    flightFilterState: Partial<FlightFilterState>,
    flightNumber: number,
  ) => filters.onFlightFilterUpdate(flightFilterState, flightNumber);

  const updateTransferFilter = (
    flightFilterState: Partial<FlightFilterState>,
  ) =>
    routes.value.forEach((_, i) =>
      filters.onFlightFilterUpdate(flightFilterState, i),
    );

  const updateAirlinesFilter = (
    state: Partial<airlineFilters.types.AirlineFiltersState>,
  ) => {
    filters.onFilterUpdate(state);
  };

  const updateFlightDuration = (flightDuration: number[]) =>
    routes.value.forEach((_, i) =>
      filters.onFlightFilterUpdate({ duration: flightDuration }, i),
    );

  const clearAirlinesFilter = () => {
    updateAirlinesFilter({
      airlines: setAllAirlines(filters.state.value.airlines, false),
      isAllAirlines: true,
      isImplicitAllAirlines: false,
    });
  };

  const clearTimeFilter = () => {
    const { start, end } = flightTimeFilter.config.DAY_IN_MINUTES;
    const defaultDepArrState = {
      time: [start, end],
      airports: [],
    };
    routes.value.forEach((_, i) => {
      updateFlightFilter(
        {
          dep: defaultDepArrState,
          arr: defaultDepArrState,
        },
        i,
      );
    });
  };

  const clearTransferFilter = () => {
    updateNightTransferFilter(false);

    const flights = props.offers.flatMap(offer => offer.flights);
    routes.value.forEach((_, i) => {
      updateFlightFilter(
        {
          transfers: [],
          // @ts-expect-error TODO: create local adapter to negotiate with overlapped types
          transfersDuration: getTransfersDurationRange(flights),
        },
        i,
      );
    });
  };

  const resetSelectedFilters = () => {
    filters.onFilterUpdate(committedFilterState.value);
  };

  const commitFilters = () => {
    committedFilterState.value = filters.state.value;
    filtersBottomSheet.hide();

    const currentState = filters.state.value as ExtendedFilterState;

    if (openedFromMainFilters.value) {
      analyticsEventsLib.applyFiltersClick(filterParams.value);
    } else {
      const stage = filterStage.filterStage.value;
      if (stage === filtersConfig.FILTER_STAGES.AIRLINES) {
        analyticsEventsLib.airlinesSeparateApplied(
          getAirlinesSeparateAppliedParams(currentState),
        );
      } else if (stage === filtersConfig.FILTER_STAGES.TIME) {
        analyticsEventsLib.timeSeparateApplied(
          getTimeSeparateAppliedParams(currentState),
        );
      } else if (stage === filtersConfig.FILTER_STAGES.TRANSFERS) {
        analyticsEventsLib.transfersSeparateApplied(
          getTransfersSeparateAppliedParams(currentState),
        );
      } else {
        analyticsEventsLib.applyFiltersClick(filterParams.value);
      }
    }

    emit(
      'onOffersFilter',
      filteredOffers.value,
      filterParams.value,
      hasFilter.value,
    );
    sentryModel.addBreadcrumb({
      category: sentryModel.breadcrumbCategories.SEARCH,
      message: 'Filters applied',
      level: sentryModel.level.INFO,
    });
  };

  /** Couldn't implement using ref because of problems with conditional rendering */
  onMounted(() => {
    if (route.query.iata) {
      updateAirlinesFilter({
        isAllAirlines: false,
        isImplicitAllAirlines: false,
        airlines: {
          ...filters.state.value.airlines,
          [route.query.iata as string]: true,
        },
      });
      commitFilters();
    } else {
      // Emit initial state
      emit(
        'onOffersFilter',
        filteredOffers.value,
        filterParams.value,
        hasFilter.value,
      );
    }
    resultsHeaderHeight.value = Number(
      document.getElementById('resultsHeader')?.offsetHeight,
    );
  });

  onBeforeMount(() => {
    initFiltersState();
    committedFilterState.value = filters.state.value; // Reinitialize commited filter state to match the default state
  });
</script>
